import React, { useState } from "react";
import { Plus, X, Archive } from "lucide-react";
import { TextArea, SmallBtn } from "@/components/praticien/fiche/ui";
import { THEME_SUGGESTIONS } from "@/data/fiche";

export const Thematiques = ({ value, onChange, lang, T }) => {
  const cur = value.current || { themes: [], commentaire: "", objectifProchaine: "" };
  const history = value.history || [];
  const [input, setInput] = useState("");

  const setCur = (patch) => onChange({ ...value, current: { ...cur, ...patch } });
  const addTheme = (label) => {
    const t = (label || "").trim();
    if (!t || cur.themes.includes(t)) return;
    setCur({ themes: [...cur.themes, t] });
    setInput("");
  };
  const removeTheme = (t) => setCur({ themes: cur.themes.filter((x) => x !== t) });

  const archive = () => {
    const snapshot = { ...cur, archivedAt: new Date().toISOString() };
    onChange({ ...value, history: [snapshot, ...history] });
  };

  return (
    <div>
      <div className="rounded-2xl bg-[#F4E4D0] border border-[var(--gold-light)] p-3 mb-5 flex items-start gap-2" data-testid="fiche-thematiques-warning">
        <span className="text-[var(--gold)] mt-0.5">⚠️</span>
        <p className="text-xs text-[var(--ink)] leading-relaxed">{T.thematiquesWarning}</p>
      </div>

      <label className="block text-sm font-medium text-[var(--ink)] mb-1.5">{T.selectedThemes}</label>
      <div className="flex flex-wrap gap-2 mb-3" data-testid="fiche-themes-selected">
        {cur.themes.map((t) => (
          <span key={t} className="inline-flex items-center gap-1.5 rounded-full bg-[var(--gold)] text-white text-sm px-3 py-1">
            {t}
            <button onClick={() => removeTheme(t)} data-testid={`fiche-theme-remove-${t}`} className="hover:opacity-70"><X size={13} /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2 mb-3">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTheme(input); } }}
          placeholder={T.themePlaceholder}
          data-testid="fiche-theme-input"
          className="flex-1 rounded-xl border border-[var(--border)] bg-white/80 px-3.5 py-2 text-sm text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--gold-light)]"
        />
        <SmallBtn onClick={() => addTheme(input)} testid="fiche-theme-add" tone="solid"><Plus size={14} /> {T.addTheme}</SmallBtn>
      </div>
      <div className="mb-5">
        <span className="text-xs text-[var(--ink-soft)] block mb-1.5">{T.suggestions} :</span>
        <div className="flex flex-wrap gap-1.5">
          {THEME_SUGGESTIONS[lang].map((s) => (
            <button key={s} onClick={() => addTheme(s)} data-testid={`fiche-theme-sugg-${s}`}
              className="text-xs px-2.5 py-1 rounded-full border border-[var(--border)] text-[var(--ink-soft)] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors">
              + {s}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        <TextArea label={T.themeComment} value={cur.commentaire} onChange={(v) => setCur({ commentaire: v })} testid="fiche-theme-comment" rows={2} />
        <TextArea label={T.objectifProchaine} value={cur.objectifProchaine} onChange={(v) => setCur({ objectifProchaine: v })} testid="fiche-theme-objectif-prochaine" rows={2} />
      </div>

      <div className="mt-4">
        <SmallBtn onClick={archive} testid="fiche-theme-archive"><Archive size={14} /> {T.archiveTheme}</SmallBtn>
      </div>

      <div className="mt-6 pt-5 border-t border-[var(--border)]">
        <h3 className="font-serif-title text-lg text-[var(--gold)] mb-3">{T.themeHistory}</h3>
        {history.length === 0 ? (
          <p className="text-sm text-[var(--ink-soft)] italic">{T.noHistory}</p>
        ) : (
          <div className="space-y-3" data-testid="fiche-theme-history">
            {history.map((h, i) => (
              <div key={i} className="rounded-2xl border border-[var(--border)] bg-white/50 p-3" data-testid={`fiche-theme-history-${i}`}>
                <div className="text-xs text-[var(--ink-soft)] mb-1.5">{T.archivedAt} {new Date(h.archivedAt).toLocaleString(lang === "es" ? "es-ES" : "fr-FR")}</div>
                <div className="flex flex-wrap gap-1.5 mb-1.5">
                  {(h.themes || []).map((t) => (<span key={t} className="text-xs rounded-full bg-[var(--beige)] px-2 py-0.5 text-[var(--ink)]">{t}</span>))}
                </div>
                {h.commentaire && <p className="text-xs text-[var(--ink-soft)]"><strong>{T.themeComment} :</strong> {h.commentaire}</p>}
                {h.objectifProchaine && <p className="text-xs text-[var(--ink-soft)]"><strong>{T.objectifProchaine} :</strong> {h.objectifProchaine}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
